import React, { useEffect, useState, memo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { format, parse, add, differenceInCalendarMonths } from 'date-fns';
import { getDateByformat, isBeforeDay } from '@common/utils/date';
import SatModal from '@components/common/satUI/SatModal';

const stateSelector = state => state.userReducer;

const HomeMainUserEntitlePopup = props => {
  const { channelInfo } = $hooks.useUser();

  // DM 채널인지 여부
  const isRestrictedUser = $utils.checkRestrictedUser(channelInfo);

  return isRestrictedUser ? (
    <HomeMainUserEntitlePopupDM {...props} />
  ) : (
    <HomeMainUserEntitlePopupGeneral {...props} />
  );
};

const HomeMainUserEntitlePopupGeneral = memo(
  ({ isOpen, popupSaveKey, onClose, onStopDisplay }) => {
    const { error: toastError } = $hooks.useToast();
    const { channelInfo } = $hooks.useUser();
    const { getUserEntitle, getMainLinkToken } = $hooks.useApi();

    const { tokenInfo } = useSelector(stateSelector);
    const [entitleInfo, setEntitleInfo] = useState({});

    const {
      lifeEndDate,
      thirdEndDate,
      variableEndDate,
      varSalePsblYn,
      isLifeWillExpire,
      isThirdWillExpire,
      isVariableWillExpire,
      isLifeExpired,
      isThirdExpired,
      isVariableExpired,
      isEducationDisplay,
    } = entitleInfo;

    const parseEntitleInfo = serverEntitleInfo => {
      const todayDate = new Date();

      // 자격증명 만료 알림 시작일 (1달전)
      const EXPIRE_ALERT_MONTH_BEFORE = 1;
      // 교육 알림 Display 기준 (1달전 ~ 만료일)
      const EDUCATION_DISPLAY_MONTH_BEFORE = 1;
      // 자격증명 날짜 형식
      const ENTITLE_DATE_FORMAT = 'yyyyMMdd';

      const {
        lifeInsrnQualEndPrargDd: lifeEndDate,
        thinsQualEndPrargDd: thirdEndDate,
        varSaleEndDd: variableEndDate,
        cpstEduStrtDd: educationStartDate,
        cpstEduEndDd: educationEndDate,
      } = serverEntitleInfo;

      const lifeExpireRemaining = differenceInCalendarMonths(
        parse(lifeEndDate?.replace(/-/g, ''), ENTITLE_DATE_FORMAT, new Date()),
        todayDate,
      );
      const thirdExpireRemaining = differenceInCalendarMonths(
        parse(thirdEndDate?.replace(/-/g, ''), ENTITLE_DATE_FORMAT, new Date()),
        todayDate,
      );
      const variableExpireRemaining = differenceInCalendarMonths(
        parse(
          variableEndDate?.replace(/-/g, ''),
          ENTITLE_DATE_FORMAT,
          new Date(),
        ),
        todayDate,
      );

      const isLifeWillExpire = lifeExpireRemaining < EXPIRE_ALERT_MONTH_BEFORE;
      const isThirdWillExpire =
        thirdExpireRemaining < EXPIRE_ALERT_MONTH_BEFORE;
      const isVariableWillExpire =
        variableExpireRemaining < EXPIRE_ALERT_MONTH_BEFORE;

      const isLifeExpired = lifeExpireRemaining < 0;
      const isThirdExpired = thirdExpireRemaining < 0;
      const isVariableExpired = variableExpireRemaining < 0;
      const isEducationDisplay = $utils.isTodayMonthBetween(
        educationStartDate,
        educationEndDate,
        EDUCATION_DISPLAY_MONTH_BEFORE,
      );

      return {
        ...serverEntitleInfo,
        lifeEndDate,
        thirdEndDate,
        variableEndDate,
        isLifeWillExpire,
        isThirdWillExpire,
        isVariableWillExpire,
        isLifeExpired,
        isThirdExpired,
        isVariableExpired,
        isEducationDisplay,
      };
    };

    const getExpiredText = (isExpired, isWillExpire) => {
      if (isExpired) return '/ 만료';
      else if (isWillExpire) return '/ 예정';
      return '';
    };

    const getExpiredStyle = (isExpired, isWillExpire) => {
      if (isExpired) return 'c-red';
      else if (isWillExpire) return 'c-orange';
      return '';
    };

    // 모집제한일
    const getRecruitmentLimitDate = (
      lifeInsrnQualEndPrargDd,
      thinsQualEndPrargDd,
    ) => {
      const lifeInsExpireDateFormat = parse(
        lifeInsrnQualEndPrargDd,
        'yyyyMMdd',
        new Date(),
      );
      const thinsQualEndPrargDdFormat = parse(
        thinsQualEndPrargDd,
        'yyyyMMdd',
        new Date(),
      );

      const isBeforeLifeInsExpireDate = isBeforeDay(
        lifeInsExpireDateFormat,
        thinsQualEndPrargDdFormat,
      );

      // 앞선 날짜
      const beforeDay = isBeforeLifeInsExpireDate
        ? lifeInsExpireDateFormat
        : thinsQualEndPrargDdFormat;
      // 앞선 날짜 + 10
      return format(add(beforeDay, { days: 10 }), 'yyyy년 MM월 dd일');
    };

    const openVideoEducation = async () => {
      const token = await $utils.getLinkToken(getMainLinkToken);
      if (!token) return toastError('링크 정보를 가져오는데 실패하였습니다.');

      const url = $utils.getVideoEducationUrl('variable', token);
      $utils.openUrl(url);
    };

    const onMoveLinkClick = () => {
      const linkUrl = 'https://www.in.or.kr';
      $utils.openUrl(linkUrl, 'outLink');
    };

    const handleStopDisplay = () => {
      onStopDisplay(popupSaveKey, true);
      onClose();
    };

    const showEntitlePopup = () => {
      // 자격증명 팝업은 GA 채널만 보이도록 함
      const isGA = $utils.checkGA(channelInfo.cdVal);
      if (isGA) fetchEntitle();
      else onClose();
    };

    const fetchEntitle = async () => {
      const agentNo = tokenInfo?.empno;
      const response = await getUserEntitle({
        params: { agentNo },
      });

      const isSuccess = $utils.checkApiSuccess(response);
      if (isSuccess) {
        const data = $utils.getApiData(response);
        const isData = Object.values(data).length > 0;

        if (isData) {
          const parsedEntitleInfo = parseEntitleInfo(data);
          setEntitleInfo(parsedEntitleInfo);
        } else {
          console.log('### 자격증명 팝업: 내용 없음 (팝업 자동 종료) - ', data);
          onClose();
        }
      } else {
        console.log('### 자격증명 팝업: API 실패 (팝업 자동 종료)');
        onClose();
      }
    };

    useEffect(showEntitlePopup, []);

    return (
      entitleInfo && (
        <SatModal visible={isOpen} setVisible={onClose} wide>
          <header className="modal-header">
            <h3 className="layer-tit2">자격정보 만료 (예정)</h3>
          </header>

          <div className="cont">
            {/* 자격정보 만료 (예정) Table  */}
            <table className="tbl-sty4 border-light-gray">
              <thead>
                <tr>
                  <th scope="col" style={{ width: '33%' }}>
                    생명보험 자격만료예정일
                  </th>
                  <th scope="col" style={{ width: '33%' }}>
                    제3보험 자격만료예정일
                  </th>
                  <th scope="col">변액자격만료예정일</th>
                </tr>
              </thead>

              <tbody>
                <tr className="text-center">
                  <td>
                    <span
                      className={`bold ${getExpiredStyle(
                        isLifeExpired,
                        isLifeWillExpire,
                      )}`}
                    >
                      {`${getDateByformat(
                        lifeEndDate,
                        'yyyy.MM.dd',
                      )} ${getExpiredText(isLifeExpired, isLifeWillExpire)}`}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`bold ${getExpiredStyle(
                        isThirdExpired,
                        isThirdWillExpire,
                      )}`}
                    >
                      {`${getDateByformat(
                        thirdEndDate,
                        'yyyy.MM.dd',
                      )} ${getExpiredText(isThirdExpired, isThirdWillExpire)}`}
                    </span>
                  </td>

                  {varSalePsblYn === 'Y' ? (
                    <td>
                      <span
                        className={`bold ${getExpiredStyle(
                          isVariableExpired,
                          isVariableWillExpire,
                        )}`}
                      >
                        {`${getDateByformat(
                          variableEndDate,
                          'yyyy.MM.dd',
                        )} ${getExpiredText(
                          isVariableExpired,
                          isVariableWillExpire,
                        )}`}
                      </span>
                    </td>
                  ) : (
                    <td>
                      <span className="bold">변액 자격 없음</span>
                    </td>
                  )}
                </tr>
              </tbody>
            </table>

            {/* 생보(제3보) 자격 보수교육 및 자격만료(예정) 안내 */}
            {isEducationDisplay && (
              <div className="info-box mgb-20">
                <h4 className="tit-mini mgt-0 mgb-8">
                  생보(제3보) 자격 보수교육 및 자격만료(예정) 안내
                </h4>
                <p className="text-info2 asterisk-type">
                  {`교육이수기간 : ${getDateByformat(
                    entitleInfo.cpstEduStrtDd,
                    'yyyy.MM.dd',
                  )} ~ ${getDateByformat(
                    entitleInfo.cpstEduEndDd,
                    'yyyy.MM.dd',
                  )}`}
                </p>

                <p className="text-info2 asterisk-type">
                  <span className="c-orange">
                    {`보수교육이 완료되지 않을 경우 ${getRecruitmentLimitDate(
                      entitleInfo.lifeInsrnQualEndPrargDd,
                      entitleInfo.thinsQualEndPrargDd,
                    )}부터 모집이 제한`}
                  </span>
                  됩니다.
                </p>
                <p className="text-info2 asterisk-type">
                  모집제한 이후 교육 완료시, 교육이수완료일 +1일부터 모집이
                  가능합니다.
                </p>
                <p className="text-info2 asterisk-type">
                  보험연수원을 통해 이수할 수 있습니다. (☎1588-3055 보험연수원)
                </p>

                <div className="box-btns-center mgt-8">
                  <button
                    type="button"
                    className="btn-s blue btn-wide"
                    onClick={onMoveLinkClick}
                  >
                    보험연수원(www.in.or.kr)접속
                  </button>
                </div>
              </div>
            )}

            {/* 변액자격 갱신을 위한 보수교육 진행 */}
            {varSalePsblYn === 'Y' && isVariableWillExpire && (
              <div className="info-box mgb-0">
                <h4 className="tit-mini mgt-0 mgb-8">
                  변액자격 갱신을 위한 보수교육 진행
                </h4>
                <p className="text-info2 asterisk-type mt-8">
                  동영상 이수를 통해 변액보수교육을 이수할 수 있습니다.
                </p>

                <div className="box-btns-center mgt-8">
                  <button
                    type="button"
                    className="btn-s blue btn-wide"
                    onClick={openVideoEducation}
                  >
                    동영상이수
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="box-btns-center mgt-16">
            <div className="check-wrap chk-style">
              <input id="today" type="checkbox" onChange={handleStopDisplay} />
              <label htmlFor="today">오늘 하루 다시 보지 않기</label>
            </div>
          </div>
        </SatModal>
      )
    );
  },
);

const HomeMainUserEntitlePopupDM = ({ onClose }) => {
  const { alert } = $hooks.useModal();
  const { error: toastError } = $hooks.useToast();
  const { getEduNotification } = $hooks.useApi();
  const { userInfo } = $hooks.useUser();

  const [messageInfos, setMessageInfos] = useState([]);

  const showMessages = (index = 0) => {
    const messageInfo = messageInfos[index];
    if (!messageInfo) return;

    const { ALERT_MSG: message } = messageInfo;

    alert({
      message,
      onClose: () => {
        const isNextMessageExist = index < messageInfos.length - 1;
        if (isNextMessageExist) showMessages(index + 1);
        else onClose();
      },
    });
  };

  const fetchEduNotification = async (eduCode, productCode) => {
    const { userId } = userInfo;

    const payload = {
      SAWON_CD: userId, // 설계사번호
      EDUC_GUBUN: eduCode, // 교육구분
      PROD_CD: productCode, // 상품코드 (교육구분이 '3'(상품교육) 일때만 포함)
    };
    const response = await getEduNotification(payload);

    const isSuccess = $utils.checkApiSuccess(response);
    if (isSuccess) {
      const data = $utils.getApiData(response);
      const results = data?.MB_RESULT?.result || [];

      const messages = results.filter(({ SALE_YN }) => {
        // SALE_YN이 'N' 이거나 '0' 인 것들만 노출
        const isShowMessage = SALE_YN === 'N' || SALE_YN === '0';
        return isShowMessage;
      });

      return messages;
    } else {
      toastError('교육이수 정보를 가져오는데 실패하였습니다.');
      return [];
    }
  };

  const checkEduNotification = async () => {
    const eduCodes = ['1', '2'];
    const fetchPromises = eduCodes.map(eduCode =>
      fetchEduNotification(eduCode),
    );
    const results = await Promise.all(fetchPromises);
    const messageInfos = results.flatMap(messageInfo => messageInfo);
    const isMessageExist = messageInfos?.length > 0;

    if (isMessageExist) setMessageInfos(messageInfos);
    else onClose();
  };

  useEffect(checkEduNotification, []);
  useEffect(showMessages, [messageInfos]);

  return <></>;
};

HomeMainUserEntitlePopup.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired, // 팝업닫기 버튼 클릭 핸들러
  onStopDisplay: PropTypes.func,
  popupSaveKey: PropTypes.string,
};

HomeMainUserEntitlePopup.defaultProps = {
  isOpen: false,
  popupSaveKey: 'hideEntitlePopup',
};

export default memo(HomeMainUserEntitlePopup);
